import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Select, DatePicker, Button, Spin, Alert, Space, message } from "antd";
import { FilePdfOutlined, FileTextOutlined } from "@ant-design/icons";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { getSummaryEmployee } from "../../redux/slieces/summaryEmplyeeSlice";
import {
  getWorkStatusReqeustsInOrg,
  getWorkStatusRequestByUserId,
} from "../../redux/slieces/workStatusAppSlice";
import { buildReport, distinctWeeks, attachNotices } from "../../core/report/reportBuilder";
import { fetchWeekPlans } from "../../core/report/weekPlanNotices";
import { exportElementToPdf } from "../../core/report/exportPdf";
import ReportView from "./ReportView";

const { RangePicker } = DatePicker;

const ReportTab = () => {
  const { keycloak, initialized } = useKeycloak();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportRef = useRef(null);

  const isAdmin = initialized && keycloak?.hasRealmRole?.("mediadmin");

  const {
    summaryEmployee = [],
    loading: summaryLoading = false,
    error: summaryError = null,
  } = useSelector((state) => state.summaryEmployee || {});
  const {
    workStatusRequests = [],
    loading: appsLoading = false,
    error: appsError = null,
  } = useSelector((state) => state.workStatusApp || {});

  const [selectedEmails, setSelectedEmails] = useState([]); // empty ⇒ all
  const [range, setRange] = useState([dayjs().startOf("year"), dayjs()]);
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Fetch both datasets once auth is ready. Admin gets the whole org (summary +
  // applications); a regular user gets only their own from both endpoints.
  useEffect(() => {
    if (!initialized) return;
    dispatch(getSummaryEmployee());
    dispatch(isAdmin ? getWorkStatusReqeustsInOrg() : getWorkStatusRequestByUserId());
  }, [initialized, isAdmin, dispatch]);

  const employeeOptions = useMemo(
    () =>
      (summaryEmployee || []).map((e) => ({
        label: `${e.userName} (${e.email})`,
        value: e.email,
      })),
    [summaryEmployee]
  );

  const loading = summaryLoading || appsLoading;
  const error = summaryError || appsError;

  const handleGenerate = async () => {
    if (!range || !range[0] || !range[1]) {
      message.warning(t("report.pickPeriod"));
      return;
    }
    const from = range[0].startOf("day").toDate();
    const to = range[1].endOf("day").toDate();
    const built = buildReport({
      summaryEmployees: summaryEmployee,
      applications: workStatusRequests,
      // A user is server-scoped to self already; for an admin, empty selection
      // means "all employees".
      employeeEmails: isAdmin && selectedEmails.length ? selectedEmails : null,
      from,
      to,
    });

    // Week-plan comments ("notices") aren't in the summary payload — fetch the
    // week plans for the weeks the report covers, then fold them in.
    setGenerating(true);
    try {
      const weekPlans = await fetchWeekPlans(distinctWeeks(built.employees));
      setReport(attachNotices(built, weekPlans, { from, to }));
    } catch (e) {
      // Notices are supplementary — still show the report, just warn.
      message.error(t("report.noticesError"));
      setReport(built);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const name = `report_${range[0].format("YYYY-MM-DD")}_${range[1].format(
        "YYYY-MM-DD"
      )}.pdf`;
      await exportElementToPdf(reportRef.current, name);
    } catch (e) {
      message.error(t("report.exportError"));
    } finally {
      setExporting(false);
    }
  };

  if (!initialized || loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Card>
        <Space wrap size="large" align="end">
          {isAdmin && (
            <div>
              <div>{t("report.selectEmployees")}</div>
              <Space.Compact>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ minWidth: 280 }}
                  placeholder={t("report.allEmployees")}
                  value={selectedEmails}
                  onChange={setSelectedEmails}
                  options={employeeOptions}
                  maxTagCount="responsive"
                  data-testid="report.employeeSelect"
                />
                <Button
                  onClick={() => setSelectedEmails(employeeOptions.map((o) => o.value))}
                  data-testid="report.selectAll"
                >
                  {t("report.selectAll")}
                </Button>
              </Space.Compact>
            </div>
          )}
          <div>
            <div>{t("report.period")}</div>
            <RangePicker
              format="DD.MM.YYYY"
              value={range}
              onChange={(dates) => setRange(dates)}
              allowClear={false}
            />
          </div>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={handleGenerate}
            loading={generating}
            data-testid="report.generate"
          >
            {t("report.generate")}
          </Button>
          <Button
            icon={<FilePdfOutlined />}
            onClick={handleDownload}
            disabled={!report || report.employees.length === 0}
            loading={exporting}
            data-testid="report.download"
          >
            {t("report.downloadPdf")}
          </Button>
        </Space>
      </Card>

      {error && (
        <Alert type="error" showIcon style={{ marginTop: 16 }} message={t("report.loadError")} />
      )}

      {report && (
        <div style={{ marginTop: 16 }}>
          <ReportView ref={reportRef} report={report} />
        </div>
      )}
    </div>
  );
};

export default ReportTab;
